import { z } from 'zod';
import { ApiError } from './errors.js';
import { OpaqueRef } from './ids.js';
import { Cents, TimeOfDay, Weekday } from './primitives.js';
/**
 * Departures (spec §4.8) — the island's own shape.
 *
 * "Tours, boat charters, dive trips, ferries, fitness classes — **a scheduled
 * instance with shared per-seat capacity**." Two things make a departure
 * different from everything else this vocabulary already describes, and the spec
 * names both precisely:
 *
 *  1. **Time is an instance, not a window.** The 9:00 snorkel trip *exists*:
 *     generated from a template, cancellable as a thing, carrying a manifest.
 *     What lives here is the template — which weekdays, at what times, inside
 *     which season — and the status an instance can be in.
 *  2. **A booking consumes `party_size` units, not one.** So a ticket is priced
 *     *per seat*, and the tiers a party picks are the modifier machinery of
 *     migration 0014 wearing a different hat. `priceDepartureSeats` is the one
 *     place that arithmetic happens, and there is deliberately no parameter
 *     through which a caller could name a cent (B7).
 *
 * Nothing in this file knows about Postgres, HTTP or the engine. The template is
 * *expanded* into instants by `@yourstore/scheduling` (timezone arithmetic is
 * its job and nobody else's), and the instances are materialized by
 * `@yourstore/store`.
 */
/* ------------------------------------------------------------------ */
/* The schedule template                                               */
/* ------------------------------------------------------------------ */
/**
 * One weekday's departures — a list of **start times**, not of windows.
 *
 * This is the one place the departures vertical declines to reuse `WeekHours`,
 * and the reason is worth stating because dev.md's rule is otherwise absolute
 * ("time-of-day availability is always a `WeekHours`/`DayHours` document, never
 * a bespoke shape"). That rule is about *availability*: "is this orderable at
 * 13:40 on a Tuesday" is "are we open", and one property-tested interval algebra
 * answers both. A departure asks neither question. It has **one instant**, and
 * its span is `duration_min` — a number that already exists, once, on the
 * satellite.
 *
 * Storing `{start: "09:00", end: "12:30"}` would therefore put the trip's length
 * in two places, and the day somebody edits `duration_min` from 210 to 240 the
 * document silently disagrees with the row that owns it. There is no algebra to
 * gain either: a departure template is never intersected with anything. What is
 * kept is the *discipline* — Monday-first, seven entries, one zod schema, a
 * small document read whole with its row — which is what made `WeekHours` worth
 * having in the first place.
 *
 * Times are ascending and unique because a boat cannot leave twice at nine, and
 * because instance generation keys on `(item, departure instant)`: a duplicate
 * would be an idempotent no-op that looked like a lost sailing.
 */
export const DepartureDay = z.object({
    weekday: Weekday,
    /** `HH:MM` in the tenant's timezone, ascending and unique. Empty = no sailing. */
    departures: z
        .array(TimeOfDay)
        .default([])
        .refine((times) => times.every((t, i) => i === 0 || t > times[i - 1]), {
        message: 'departure times must be ascending and unique',
    }),
});
/** Monday-first, seven entries — the same discipline `WeekHours` keeps. */
export const DepartureSchedule = z.array(DepartureDay).length(7);
/**
 * A season, as **month and day** — `11-15` to `04-30`.
 *
 * A season recurs annually: "we run the afternoon snorkel from mid-November to
 * the end of April" is a fact about the calendar, not about 2026. Absolute dates
 * would need a row per year, and the fleet alone seeds a year of history plus a
 * forward book that crosses the boundary — so the first `--today` past the end
 * date would silently stop generating sailings that a real operator still runs.
 *
 * A window whose end sorts *before* its start wraps the New Year, which is what
 * a Caribbean high season actually does. `inSeason` handles both directions, and
 * there is a property test that says a year-round item and a full-circle season
 * answer identically.
 */
export const MonthDay = z
    .string()
    .regex(/^(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/, 'must be MM-DD');
export const Season = z.object({ from: MonthDay, to: MonthDay });
/**
 * Does `isoDay` (a `YYYY-MM-DD` in the tenant's zone) fall inside the season?
 *
 * `null` is year-round, which is a different fact from a season covering every
 * day: the first is "this item has no season", the second is an operator who
 * typed one. Both answer `true` here; only the first says so without a document.
 *
 * Inclusive at both ends. A season is spoken about the way a person speaks about
 * one — "through the end of April" includes April the thirtieth — and the
 * half-open convention this system uses everywhere else is about *instants*,
 * where the boundary is a real moment two intervals could both claim. A month
 * and a day are not an instant.
 */
export function inSeason(season, isoDay) {
    if (season === null)
        return true;
    const monthDay = isoDay.slice(5);
    if (season.from <= season.to)
        return monthDay >= season.from && monthDay <= season.to;
    // Wrapped: November through April is "on or after Nov 15, or on or before Apr 30".
    return monthDay >= season.from || monthDay <= season.to;
}
/* ------------------------------------------------------------------ */
/* The instance                                                        */
/* ------------------------------------------------------------------ */
/**
 * What a materialized sailing can be.
 *
 * `cancelled_weather` is a distinct value from `cancelled` because spec §4.8
 * calls the weather day "the vertical's defining operational event" and the two
 * are different conversations: a weather call is nobody's fault, is announced to
 * a manifest at once, and is the case the operator's rebook flow exists for; a
 * plain cancellation is the shop deciding not to run a trip. Collapsing them
 * would make "how often were we blown out this season" unanswerable, which is
 * the first question a dive shop's owner asks in April.
 */
export const DepartureInstanceStatus = z.enum([
    'scheduled',
    'cancelled_weather',
    'cancelled',
    'completed',
]);
/** The statuses whose seats are still sellable. */
export const SELLABLE_DEPARTURE_STATUSES = ['scheduled'];
/** The statuses that mean the boat is not going out. */
export const CANCELLED_DEPARTURE_STATUSES = [
    'cancelled_weather',
    'cancelled',
];
export function isCancelledDeparture(status) {
    return CANCELLED_DEPARTURE_STATUSES.includes(status);
}
/* ------------------------------------------------------------------ */
/* Ticket tiers (spec §4.8 — "the modifier machinery wearing a hat")   */
/* ------------------------------------------------------------------ */
/**
 * One ticket type, as the catalog holds it: a `modifiers` row inside the item's
 * required "ticket type" `modifier_groups` row.
 *
 * `price_delta_cents` is signed for exactly the reason 0014 gave the column:
 * a child's ticket is a *credit* against the adult price, and a column that
 * could not express it would push that discount somewhere §7.4 rule 3 cannot
 * see it.
 */
export const TicketTier = z.object({
    id: OpaqueRef,
    name: z.string().min(1),
    price_delta_cents: Cents,
    sold_out: z.boolean().default(false),
});
/** "Two adults and a child" — what a caller may say. Note there is no price. */
export const TicketPick = z.object({
    tier_id: OpaqueRef,
    seats: z.number().int().min(1).max(50),
});
/**
 * Price a party's seats from the catalog and **nothing else** (B7).
 *
 * There is no parameter for an amount, and there is not going to be one: the
 * caller on the partner side is an LLM agent and it is persuadable, so the
 * absence has to be structural rather than a rule somebody remembers. The
 * request names tiers and counts; this function names the money.
 *
 * Three refusals, each of them a thing a real caller gets wrong:
 *
 *  * a tier that is not on this item — an id from another trip, or invented;
 *  * a sold-out tier — the shop is out of child gear this morning;
 *  * a seat priced below zero — a delta larger than the base fare, which is a
 *    catalog mistake rather than a free ticket. A *line* may be negative on an
 *    order (0014's reasoning), because a bill is checked as a whole; a seat is
 *    the whole of its own bill, so it is checked here.
 */
export function priceDepartureSeats(basePriceCents, tiers, picks) {
    if (picks.length === 0) {
        throw ApiError.invalid('A departure booking needs at least one ticket.');
    }
    const byId = new Map(tiers.map((t) => [t.id, t]));
    const seen = new Set();
    const lines = [];
    let seats = 0;
    let totalCents = 0;
    for (const pick of picks) {
        const tier = byId.get(pick.tier_id);
        if (tier === undefined) {
            throw ApiError.invalid('That ticket type is not sold on this departure.', {
                tier_id: pick.tier_id,
            });
        }
        if (tier.sold_out) {
            throw ApiError.invalid(`${tier.name} is not available on this departure.`, {
                tier_id: pick.tier_id,
            });
        }
        if (seen.has(tier.id)) {
            // Two lines of the same tier is the same fact said twice, and it would
            // make a manifest read as two parties. Say it once, with a count.
            throw ApiError.invalid('That ticket type is named twice; give it one seat count.', {
                tier_id: pick.tier_id,
            });
        }
        seen.add(tier.id);
        const unitPriceCents = basePriceCents + tier.price_delta_cents;
        if (unitPriceCents < 0) {
            throw ApiError.invalid('That ticket type prices a seat below zero.', {
                tier_id: pick.tier_id,
            });
        }
        lines.push({
            tierId: tier.id,
            name: tier.name,
            seats: pick.seats,
            unitPriceCents,
            lineTotalCents: unitPriceCents * pick.seats,
        });
        seats += pick.seats;
        totalCents += unitPriceCents * pick.seats;
    }
    return { seats, totalCents, lines };
}
/**
 * Price a **private charter**, which spec §4.8 says is "priced flat".
 *
 * A separate function rather than a flag on `priceDepartureSeats`, because the
 * two answer different questions and a boolean that changed which question was
 * being answered would be the worst of both. Per-seat pricing multiplies a fare
 * by a headcount; a charter has no per-seat fare at all — the boat costs what
 * the boat costs, and whether four people or six get on it changes the manifest
 * and not the bill. Multiplying a $1,450 charter by a party of five is a
 * seven-thousand-dollar mistake that looks exactly like arithmetic.
 *
 * The party still travels: `seats` is the headcount, because that is what
 * `appointments.party_size` records and what a captain counts on the dock. The
 * *line* is one boat at one price, which is what settles.
 */
export function priceDepartureCharter(basePriceCents, tiers, picks) {
    if (picks.length !== 1) {
        throw ApiError.invalid('A charter is one booking of one boat, at one price.', {
            tickets: picks.length,
        });
    }
    const pick = picks[0];
    const tier = tiers.find((t) => t.id === pick.tier_id);
    if (tier === undefined) {
        throw ApiError.invalid('That ticket type is not sold on this charter.', {
            tier_id: pick.tier_id,
        });
    }
    if (tier.sold_out) {
        throw ApiError.invalid(`${tier.name} is not available on this charter.`, {
            tier_id: pick.tier_id,
        });
    }
    const totalCents = basePriceCents + tier.price_delta_cents;
    if (totalCents < 0) {
        throw ApiError.invalid('That charter prices below zero.', { tier_id: pick.tier_id });
    }
    return {
        // The headcount, for the manifest. Seats *claimed* are the hull's lanes and
        // are the allocator's business, not the price's.
        seats: pick.seats,
        totalCents,
        // One line, quantity one: a boat. Not "five of a boat".
        lines: [
            {
                tierId: tier.id,
                name: tier.name,
                seats: 1,
                unitPriceCents: totalCents,
                lineTotalCents: totalCents,
            },
        ],
    };
}
/**
 * The fare a single seat costs at a given tier — what a quote shows beside a
 * slot before anyone has chosen how many of each.
 */
export function tierFareCents(basePriceCents, tier) {
    return Math.max(0, basePriceCents + tier.price_delta_cents);
}
/** The cheapest sellable seat on an item — the "from $X" a listing shows. */
export function cheapestFareCents(basePriceCents, tiers) {
    const sellable = tiers.filter((t) => !t.sold_out);
    if (sellable.length === 0)
        return Math.max(0, basePriceCents);
    return Math.min(...sellable.map((t) => tierFareCents(basePriceCents, t)));
}
/* ------------------------------------------------------------------ */
/* The weather policy                                                  */
/* ------------------------------------------------------------------ */
/**
 * What the shop tells a guest about being blown out, and how late a guest may
 * pull out without paying.
 *
 * Two numbers and a pointer, deliberately — **not** a fee engine. Spec §4.7
 * designs tiered cancellation policies for lodging ("free until 14 days out;
 * 50 % until 3") and §4.8 asks for none of it: a weather cancellation is the
 * *operator's* doing, so the guest owes nothing by definition, and the money
 * question is a refund the owner makes in their own PayPal dashboard (§4.7's
 * "we compute and record; we do not move money"). What a departure needs is the
 * *cut-off* — the hour by which the captain decides — and the sentence the shop
 * puts on its site.
 *
 * `policy_ref` is a pointer rather than the text itself because the text is
 * tenant content that a booking site renders and a voice agent reads aloud, and
 * tenant content is a localized document (migration 0012, docs/i18n-design.md).
 * Inlining an English sentence in a config blob would be the one place in this
 * schema where it is not.
 */
export const WeatherPolicy = z.object({
    /**
     * Hours before departure at which the shop calls it. A cancellation later than
     * this is still allowed — weather does not read a config — but this is the
     * number the site quotes and the agent repeats.
     */
    decision_hours_before: z.number().int().min(0).max(168).default(12),
    /** Hours before departure a guest may cancel free of charge. */
    free_cancellation_hours: z.number().int().min(0).max(720).default(24),
    /** A `tenant_config`/CMS key naming the localized paragraph. `''` = none written. */
    policy_ref: z.string().max(200).default(''),
});
export const DEFAULT_WEATHER_POLICY = {
    decision_hours_before: 12,
    free_cancellation_hours: 24,
    policy_ref: '',
};
/** Seats a booking of this shape claims — the whole vessel for a charter. */
export function seatsClaimedBy(isPrivate, seatsTotal, partySeats) {
    return isPrivate ? seatsTotal : partySeats;
}
/** The horizon an operator's instance generation is bounded by, in days. */
export const DEFAULT_GENERATION_HORIZON_DAYS = 90;
export const DepartureBookingRequest = z.object({
    service_id: OpaqueRef,
    instance_id: OpaqueRef,
    tickets: z.array(TicketPick).min(1),
    /** Never a price. There is no field for one, on any surface. */
    notes: z.string().max(2000).optional(),
});
export const DepartureSeatsQuery = z.object({
    service_id: OpaqueRef.optional(),
    date_from: z.string(),
    date_to: z.string(),
    party_size: z.number().int().min(1).max(50).default(1),
    limit: z.number().int().min(1).max(200).default(50),
});
/**
 * A seat count, bounded. The ceiling is a sanity rail rather than a business
 * rule: the largest passenger vessel a small operator runs is a catamaran in the
 * low hundreds, and a four-figure `seats` is a typo that would otherwise
 * generate a thousand-row claim before anyone noticed.
 */
export const SeatCount = z.number().int().min(0).max(1000);
//# sourceMappingURL=departures.js.map